type BooleanLike = string | boolean | null | undefined;

const sanitizeString = (value: string | undefined): string | undefined => {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === 'undefined' || trimmed.toLowerCase() === 'null') {
    return undefined;
  }

  return trimmed;
};

const readFromProcessEnv = (key: string): string | undefined => {
  if (typeof process === 'undefined' || typeof process.env === 'undefined') {
    return undefined;
  }

  return process.env[key];
};

const getImportMetaEnv = (): Record<string, unknown> | undefined => {
  try {
    // eslint-disable-next-line no-new-func
    const importMeta = Function('return typeof import !== "undefined" ? import.meta : undefined;')();
    if (!importMeta || typeof importMeta !== 'object') {
      return undefined;
    }
    const env = (importMeta as { env?: Record<string, unknown> }).env;
    if (!env || typeof env !== 'object') {
      return undefined;
    }
    return env;
  } catch {
    return undefined;
  }
};

const readFromImportMeta = (key: string): string | undefined => {
  const env = getImportMetaEnv();
  if (!env) {
    return undefined;
  }

  const value = env[key];
  if (typeof value === 'string') {
    return value;
  }
  if (value === undefined || value === null) {
    return undefined;
  }
  return String(value);
};

const readEnvValue = (keys: string[]): string | undefined => {
  for (const key of keys) {
    const rawValue = sanitizeString(readFromProcessEnv(key) ?? readFromImportMeta(key));
    if (rawValue !== undefined) {
      return rawValue;
    }
  }

  return undefined;
};

const parseInteger = (value: string | undefined, fallback: number): number => {
  if (!value) {
    return fallback;
  }

  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return fallback;
  }

  return parsed;
};

const parseBoolean = (value: BooleanLike, fallback: boolean): boolean => {
  if (value === undefined || value === null) {
    return fallback;
  }

  const normalized = String(value).trim().toLowerCase();
  if (['1', 'true', 'on', 'enabled', 'yes'].includes(normalized)) {
    return true;
  }
  if (['0', 'false', 'off', 'disabled', 'no'].includes(normalized)) {
    return false;
  }

  return fallback;
};

const DEFAULT_BASE_URL = 'https://api.eruptionradio.uk';

const baseUrl =
  readEnvValue(['REACT_APP_LISTENING_API_BASE_URL', 'VITE_LISTENING_API_BASE_URL']) ?? DEFAULT_BASE_URL;

const listeningEnvironment =
  readEnvValue([
    'REACT_APP_LISTENING_ENVIRONMENT',
    'VITE_LISTENING_ENVIRONMENT',
    'REACT_APP_SENTRY_ENVIRONMENT',
    'VITE_SENTRY_ENVIRONMENT',
    'SENTRY_ENVIRONMENT'
  ]) ??
  process.env.NODE_ENV ??
  'development';

const heartbeatIntervalSeconds = parseInteger(
  readEnvValue(['REACT_APP_LISTENING_HEARTBEAT_INTERVAL', 'VITE_LISTENING_HEARTBEAT_INTERVAL']),
  20
);

const enableTelemetry = parseBoolean(
  readEnvValue(['REACT_APP_LISTENING_TELEMETRY_ENABLED', 'VITE_LISTENING_TELEMETRY_ENABLED']),
  true
);

export const listeningEnv = Object.freeze({
  baseUrl,
  environment: listeningEnvironment,
  heartbeatIntervalSeconds,
  enableTelemetry: enableTelemetry && !!baseUrl
});

export type ListeningEnvironmentConfig = typeof listeningEnv;
